// @boty/database - WhatsApp Session & Creator Phone Query Utilities (Story 17.1)

import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database, WhatsappSessionInsert, WhatsappCreatorPhoneInsert } from '../types';

type DbClient = SupabaseClient<Database>;

// ============================================================================
// WHATSAPP SESSION QUERIES
// ============================================================================

/**
 * Get WhatsApp session by chat ID (phone number for DMs, group ID for groups).
 * Returns null if no session exists (uses maybeSingle).
 */
export async function getWhatsAppSession(
  client: DbClient,
  chatId: string,
) {
  const { data, error } = await client
    .from('whatsapp_sessions')
    .select('*')
    .eq('whatsapp_chat_id', chatId)
    .maybeSingle();

  if (error) throw error;
  return data;
}

/**
 * Create or update a WhatsApp session.
 * Uses upsert on whatsapp_chat_id (unique constraint) to handle re-linking.
 */
export async function upsertWhatsAppSession(
  client: DbClient,
  data: WhatsappSessionInsert,
) {
  const { data: result, error } = await client
    .from('whatsapp_sessions')
    .upsert(data, { onConflict: 'whatsapp_chat_id' })
    .select()
    .single();

  if (error) throw error;
  return result;
}

/**
 * Delete a WhatsApp session by chat ID.
 */
export async function deleteWhatsAppSession(
  client: DbClient,
  chatId: string,
) {
  const { error } = await client
    .from('whatsapp_sessions')
    .delete()
    .eq('whatsapp_chat_id', chatId);

  if (error) throw error;
}

// ============================================================================
// WHATSAPP CREATOR PHONE QUERIES
// ============================================================================

/**
 * Get a creator's linked WhatsApp phone number by user ID.
 * Returns null if no phone is linked.
 */
export async function getWhatsAppCreatorPhone(
  client: DbClient,
  userId: string,
) {
  const { data, error } = await client
    .from('whatsapp_creator_phones')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) throw error;
  return data;
}

/**
 * Find a creator by their WhatsApp phone number (reverse lookup for group auto-detection).
 * Returns the creator phone record with user_id, or null if no match.
 */
export async function getCreatorByPhone(
  client: DbClient,
  phone: string,
) {
  const { data, error } = await client
    .from('whatsapp_creator_phones')
    .select('*')
    .eq('phone_number', phone)
    .maybeSingle();

  if (error) throw error;
  return data;
}

/**
 * Create or update a creator's WhatsApp phone number.
 * Uses upsert on user_id (unique constraint).
 */
export async function upsertWhatsAppCreatorPhone(
  client: DbClient,
  data: WhatsappCreatorPhoneInsert,
) {
  const { data: result, error } = await client
    .from('whatsapp_creator_phones')
    .upsert(data, { onConflict: 'user_id' })
    .select()
    .single();

  if (error) throw error;
  return result;
}

/**
 * Delete a creator's WhatsApp phone number.
 */
export async function deleteWhatsAppCreatorPhone(
  client: DbClient,
  userId: string,
) {
  const { error } = await client
    .from('whatsapp_creator_phones')
    .delete()
    .eq('user_id', userId);

  if (error) throw error;
}
